// ---------------------------------------------------------------------------
// MoreFiltersPanel -- Main Filter Panel Component
// ---------------------------------------------------------------------------
// Side panel with 23 filter dimensions organized into 6 categories.
// Uses Sheet (drawer) layout with Apply/Cancel/Reset actions.
// ---------------------------------------------------------------------------

import React, { useEffect } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Accordion } from '@/components/ui/accordion'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  FilterRow,
  QualityToggle,
  SentimentSlider,
  SearchableMultiSelect,
  HierarchicalSelect,
  FilterSection,
} from './components'
import { useFilterPanelState } from './useFilterPanelState'
import type { MoreFiltersPanelProps, FilterCategoryKey } from './filterTypes'
import {
  GENDER_OPTIONS,
  ETHNICITY_OPTIONS,
  COUNTRY_OPTIONS,
  INDUSTRY_OPTIONS,
  LIFESTYLE_OPTIONS,
  STATUS_CLASS_OPTIONS,
  EMOTION_OPTIONS,
  LANGUAGE_OPTIONS,
  CONTENT_CATEGORY_OPTIONS,
  MEDIA_TYPE_OPTIONS,
  BRAND_CATEGORY_OPTIONS,
} from './filterConstants'

// Import parent constants for age and follower tiers
import { AGE_LABELS, FOLLOWER_TIERS } from '../constants'

// Convert parent constants to FilterOption format
const AGE_OPTIONS = AGE_LABELS.map((item) => ({
  id: item.marketing,
  label: `${item.marketing} (${item.range})`,
}))

const FOLLOWER_TIER_OPTIONS = FOLLOWER_TIERS.map((tier) => ({
  id: tier.id,
  label: `${tier.label} (${tier.range})`,
}))

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function MoreFiltersPanel({
  isOpen,
  onClose,
  committedFilters,
  onApply,
}: MoreFiltersPanelProps) {
  const {
    pendingFilters,
    updateFilter,
    resetFilters,
    syncToCommitted,
    hasChanges,
    activeFilterCount,
    getCategoryFilterCount,
  } = useFilterPanelState(committedFilters)

  // Sync to committed filters when panel opens
  useEffect(() => {
    if (isOpen) {
      syncToCommitted()
    }
  }, [isOpen, syncToCommitted])

  /**
   * Handle Apply button click.
   */
  const handleApply = () => {
    onApply(pendingFilters)
    onClose()
  }

  /**
   * Handle Reset All button click.
   */
  const handleReset = () => {
    resetFilters()
  }

  /**
   * Update array filter helper (immutable).
   */
  const updateArrayFilter = (
    category: FilterCategoryKey,
    key: string,
    value: ReadonlyArray<string>
  ) => {
    updateFilter(category, key, value)
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="right"
        hideCloseButton
        className="w-full sm:w-[380px] sm:max-w-[380px] w-[380px] p-0 flex flex-col bg-v11-surface border-v11-border"
        data-testid="more-filters-panel"
      >
        {/* Header with Actions */}
        <SheetHeader className="px-5 py-4 border-b border-v11-border space-y-3">
          {/* Row 1: Title + Close Button */}
          <div className="flex items-center justify-between">
            <div>
              <SheetTitle className="text-base font-bold text-v11-text-primary">
                More Filters
              </SheetTitle>
              <SheetDescription className="text-xs text-v11-text-muted mt-1">
                {activeFilterCount} active{' '}
                {activeFilterCount === 1 ? 'filter' : 'filters'}
              </SheetDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              aria-label="Close"
              className="h-8 w-8 text-v11-text-muted hover:text-v11-text-primary hover:bg-v11-surface-hover transition"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Row 2: Action Buttons */}
          <div className="flex items-center gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={handleApply}
              disabled={!hasChanges}
              className="flex-1 text-xs py-2 hover:opacity-90 transition"
            >
              Apply Filters
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              className="flex-1 text-xs py-2 border-v11-border text-v11-text-secondary hover:bg-v11-surface-hover hover:text-v11-text-primary transition"
            >
              Reset All
            </Button>
          </div>
        </SheetHeader>

        {/* Scrollable Filter Sections */}
        <ScrollArea className="flex-1 px-0">
          <Accordion
            type="single"
            defaultValue="demographics"
            collapsible
            className="w-full"
          >
            {/* Demographics (6 filters) */}
            <FilterSection
              id="demographics"
              title="Demographics"
              activeCount={getCategoryFilterCount('demographics')}
            >
              <div className="space-y-0">
                <FilterRow
                  label="Gender"
                  options={GENDER_OPTIONS}
                  selected={pendingFilters.demographics.gender}
                  onChange={(value) =>
                    updateArrayFilter('demographics', 'gender', value)
                  }
                />
                <FilterRow
                  label="Age"
                  options={AGE_OPTIONS}
                  selected={pendingFilters.demographics.age}
                  onChange={(value) =>
                    updateArrayFilter('demographics', 'age', value)
                  }
                />
                <FilterRow
                  label="Ethnicity"
                  options={ETHNICITY_OPTIONS}
                  selected={pendingFilters.demographics.ethnicity}
                  onChange={(value) =>
                    updateArrayFilter('demographics', 'ethnicity', value)
                  }
                />
                <FilterRow
                  label="Country"
                  options={COUNTRY_OPTIONS}
                  selected={pendingFilters.demographics.country}
                  onChange={(value) =>
                    updateArrayFilter('demographics', 'country', value)
                  }
                />
                <FilterRow
                  label="Follower Tier"
                  options={FOLLOWER_TIER_OPTIONS}
                  selected={pendingFilters.demographics.followerTier}
                  onChange={(value) =>
                    updateArrayFilter('demographics', 'followerTier', value)
                  }
                />
                <FilterRow
                  label="Industry"
                  options={INDUSTRY_OPTIONS}
                  selected={pendingFilters.demographics.industry}
                  onChange={(value) =>
                    updateArrayFilter('demographics', 'industry', value)
                  }
                />
              </div>
            </FilterSection>

            {/* Psychographics (2 filters) */}
            <FilterSection
              id="psychographics"
              title="Psychographics"
              activeCount={getCategoryFilterCount('psychographics')}
            >
              <div className="space-y-0">
                <FilterRow
                  label="Lifestyle"
                  options={LIFESTYLE_OPTIONS}
                  selected={pendingFilters.psychographics.lifestyle}
                  onChange={(value) =>
                    updateArrayFilter('psychographics', 'lifestyle', value)
                  }
                />
                <FilterRow
                  label="Status Class"
                  options={STATUS_CLASS_OPTIONS}
                  selected={pendingFilters.psychographics.statusClass}
                  onChange={(value) =>
                    updateArrayFilter('psychographics', 'statusClass', value)
                  }
                />
              </div>
            </FilterSection>

            {/* Behaviour (3 filters) */}
            <FilterSection
              id="behaviour"
              title="Behaviour"
              activeCount={getCategoryFilterCount('behaviour')}
            >
              <div className="space-y-0">
                <FilterRow
                  label="Emotions"
                  options={EMOTION_OPTIONS}
                  selected={pendingFilters.behaviour.emotions}
                  onChange={(value) =>
                    updateArrayFilter('behaviour', 'emotions', value)
                  }
                />
                <SentimentSlider
                  label="Sentiment"
                  min={0}
                  max={100}
                  value={pendingFilters.behaviour.sentiment}
                  onChange={(value) => updateFilter('behaviour', 'sentiment', value)}
                />
                <FilterRow
                  label="Language"
                  options={LANGUAGE_OPTIONS}
                  selected={pendingFilters.behaviour.language}
                  onChange={(value) =>
                    updateArrayFilter('behaviour', 'language', value)
                  }
                />
              </div>
            </FilterSection>

            {/* Content (4 filters) */}
            <FilterSection
              id="content"
              title="Content"
              activeCount={getCategoryFilterCount('content')}
            >
              <div className="space-y-0">
                <FilterRow
                  label="Categories"
                  options={CONTENT_CATEGORY_OPTIONS}
                  selected={pendingFilters.content.categories}
                  onChange={(value) =>
                    updateArrayFilter('content', 'categories', value)
                  }
                />
                <FilterRow
                  label="Media Type"
                  options={MEDIA_TYPE_OPTIONS}
                  selected={pendingFilters.content.mediaType}
                  onChange={(value) =>
                    updateArrayFilter('content', 'mediaType', value)
                  }
                />
                <QualityToggle
                  label="Paid/Organic"
                  value={pendingFilters.content.paidOrganic as any}
                  onChange={(value) => updateFilter('content', 'paidOrganic', value)}
                  comingSoon
                />
              </div>
            </FilterSection>

            {/* Entities (3 filters) */}
            <FilterSection
              id="entities"
              title="Entities"
              activeCount={getCategoryFilterCount('entities')}
            >
              <div className="space-y-0">
                <FilterRow
                  label="Brands"
                  options={[]} // TODO: Will be populated from API
                  selected={pendingFilters.entities.brands}
                  onChange={(value) =>
                    updateArrayFilter('entities', 'brands', value)
                  }
                />
                <HierarchicalSelect
                  label="Brand Category"
                  categories={BRAND_CATEGORY_OPTIONS}
                  selected={pendingFilters.entities.brandCategory}
                  onChange={(value) =>
                    updateArrayFilter('entities', 'brandCategory', value)
                  }
                />
                <FilterRow
                  label="Hashtags"
                  options={[]} // TODO: Will be populated from API
                  selected={pendingFilters.entities.hashtags}
                  onChange={(value) =>
                    updateArrayFilter('entities', 'hashtags', value)
                  }
                />
              </div>
            </FilterSection>

            {/* Quality (2 filters) */}
            <FilterSection
              id="quality"
              title="Quality"
              activeCount={getCategoryFilterCount('quality')}
            >
              <div className="space-y-0">
                <QualityToggle
                  label="Spam"
                  value={pendingFilters.quality.spam}
                  onChange={(value) => updateFilter('quality', 'spam', value)}
                />
                <QualityToggle
                  label="Toxic"
                  value={pendingFilters.quality.toxic}
                  onChange={(value) => updateFilter('quality', 'toxic', value)}
                />
              </div>
            </FilterSection>
          </Accordion>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { MoreFiltersPanel }
export type { MoreFiltersPanelProps }
